import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLenis } from "lenis/react";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Standard", href: "#manifesto" },
  { label: "Network", href: "#network" },
  { label: "Franchise", href: "#franchise" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (lenis) lenis.scrollTo(href, { offset: -10 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-[9997] transition-colors duration-500 ${
        scrolled
          ? "border-b border-white/10 bg-black/60 backdrop-blur-xl"
          : "border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-5 md:px-12">
        <button
          data-testid="brand-logo"
          onClick={() => (lenis ? lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" }))}
          className="flex items-center gap-3"
        >
          <img
            src="/carfix-logo.jpeg"
            alt="CARFIX AUTO"
            className="h-11 w-11 rounded-full object-cover md:h-12 md:w-12"
          />
          <span className="hidden font-display text-lg font-black uppercase tracking-tight text-white sm:block">
            Carfix<span className="text-[var(--volt)]"> Auto</span>
          </span>
        </button>

        <div className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="group relative font-mono text-xs uppercase tracking-[0.2em] text-white/60 transition-colors duration-300 hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[var(--volt)] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <button
          data-testid="nav-cta"
          onClick={() => go("#contact")}
          className="hidden rounded-full bg-[var(--volt)] px-6 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-black transition-transform duration-300 hover:scale-105 md:block"
        >
          Book a Bay
        </button>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="flex flex-col gap-1.5 md:hidden"
          aria-label="Toggle menu"
        >
          <span className={`h-0.5 w-6 bg-white transition-transform ${open ? "translate-y-2 rotate-45" : ""}`} />
          <span className={`h-0.5 w-6 bg-white transition-opacity ${open ? "opacity-0" : ""}`} />
          <span className={`h-0.5 w-6 bg-white transition-transform ${open ? "-translate-y-2 -rotate-45" : ""}`} />
        </button>
      </nav>

      {open && (
        <div className="flex flex-col gap-1 border-t border-white/10 bg-black/90 px-6 py-6 backdrop-blur-xl md:hidden">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-mobile-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="py-3 text-left font-display text-2xl font-bold text-white"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-mobile-cta"
            onClick={() => go("#contact")}
            className="mt-3 rounded-full bg-[var(--volt)] px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest text-black"
          >
            Book a Bay
          </button>
        </div>
      )}
    </motion.header>
  );
};
